/**
 * Saved prompts and prompt templates — the shapes and the rules, with no
 * Firestore and no Firebase anywhere near them (2026-09-17).
 *
 * A saved prompt is the PERSON'S OWN WORDS, kept so they can be used again: the
 * intake questionnaire a paralegal runs on every new matter, the demand-letter
 * brief, the clause review a partner types out three times a week. The platform
 * already generates suggestions (`useStarterSuggestions`,
 * `useThreadFollowupSuggestions`); this is the durable, person-authored
 * counterpart, and nothing in this module ever writes a sentence of its own.
 *
 * A TEMPLATE is just a prompt with blanks in it — `{{client name}}` — so there
 * is one shape, one collection and one editor, and `promptKind` is derived
 * rather than stored so the two can never disagree.
 *
 * FIREBASE-FREE BY CONTRACT. The chat-isolation suites render components that
 * reach this module, and anything that pulls `firebase/auth` into that import
 * graph touches global `fetch` at import time and takes the whole suite down
 * before a single test runs (2026-09-11). Keep this a leaf.
 */
import { boundByCodePoints, countCodePoints } from '../utils/textBounds';
import { foldCategoryName, normalizeCategoryName } from './categoryNames';

// ── Caps ────────────────────────────────────────────────────────────────────
// Every bound below counts CODE POINTS, never string length: an emoji is two
// UTF-16 units and a `slice` between them leaves a lone surrogate, which is
// valid JavaScript, invalid UTF-8 and rejected by Firestore — the write fails
// with nothing shown to the person who typed it (2026-09-16).

/** A prompt's name, as shown in the picker. */
export const PROMPT_TITLE_MAX = 80;
/** The prompt text itself. Long enough for a real brief, short enough to render. */
export const PROMPT_BODY_MAX = 8000;
/** One blank's label, e.g. `client name`. */
export const BLANK_LABEL_MAX = 40;
/**
 * Blanks in one template. Past a dozen the fill-in step stops being a quick
 * question and becomes a form, which is a different product.
 */
export const MAX_BLANKS_PER_PROMPT = 12;
/**
 * How many prompts one person may keep, and how many a workspace may publish.
 *
 * These are also what keeps a category rename — a batch write over every prompt
 * carrying that name — inside Firestore's 500-operation batch limit, so the
 * rename is one atomic write and never a half-applied one.
 */
export const MAX_PROMPTS_PER_PERSON = 200;
export const MAX_PROMPTS_PER_WORKSPACE = 500;

/** The stored shape's version, for a reader meeting a row it did not write. */
export const SAVED_PROMPT_SCHEMA_VERSION = 1;

// ── Shapes ──────────────────────────────────────────────────────────────────

/** Derived from the blanks, never stored — the two can then never disagree. */
export type PromptKind = 'prompt' | 'template';

export interface SavedPrompt {
  id: string;
  title: string;
  /** The prompt text. May contain `{{blanks}}`. */
  body: string;
  /**
   * The category NAME this prompt is filed under, '' when unfiled.
   *
   * A name, not an id, because the category list is DERIVED from the prompts
   * themselves rather than kept in a second document. There is no registry to
   * drift from, no orphaned id, and no empty category to garbage-collect — and
   * the fold rule (`foldCategoryName`) is what makes "Intake" and "intake" one
   * group, exactly as it does for thread sections.
   */
  category: string;
  /** The blanks found in `body`, in first-appearance order. Stored so the picker can say "3 blanks" without re-parsing every row. */
  blanks: string[];
  createdAt: number;
  updatedAt: number;
  /** Who wrote it. Travels with the prompt if it is later shared with the workspace. */
  createdBy: string;
  /** Last time it was actually used, which is what the picker orders by. */
  lastUsedAt?: number;
  useCount?: number;
  schemaVersion?: number;
}

/** What a person hands the editor. Everything else is the machinery's job. */
export interface SavedPromptDraft {
  title: unknown;
  body: unknown;
  category?: unknown;
}

/** A refusal a person reads, or a value. Never a thrown string. */
export type PromptProblem =
  | 'empty-title'
  | 'empty-body'
  | 'body-too-long'
  | 'too-many-blanks'
  | 'library-full';

export type Verdict<T> = { ok: true; value: T } | { ok: false; problem: PromptProblem };

export interface NormalizedPrompt {
  title: string;
  body: string;
  category: string;
  blanks: string[];
}

// ── Blanks ──────────────────────────────────────────────────────────────────

/**
 * `{{ client name }}` — double braces around free text.
 *
 * DELIBERATELY FLAT NAMES ONLY. The dead `PromptTemplateManager` stub this
 * replaces walked dotted paths (`{{a.b.c}}`) against a context object, which
 * has no counterpart here: the person fills a FORM, one labelled field per
 * blank. A dotted path renders a field labelled "client.name" — worse copy than
 * the plain `{{client name}}` — and cannot express a name with a space at all.
 * The stub had zero call sites, so no stored template anywhere uses one, and a
 * dotted name in a person-authored template is far more likely a typo than an
 * intent. A blank containing a brace or a newline is not a blank.
 */
const BLANK_PATTERN = /\{\{([^{}\n]{1,80}?)\}\}/g;

/** One blank's label as stored and shown: trimmed, single-spaced, bounded. */
export function normalizeBlankLabel(raw: unknown): string {
  return boundByCodePoints(
    (typeof raw === 'string' ? raw : '').trim().replace(/\s+/g, ' '),
    BLANK_LABEL_MAX,
  );
}

/**
 * The blanks in `body`, in FIRST-APPEARANCE order, de-duplicated by the same
 * case- and accent-insensitive fold category names use.
 *
 * De-duplication is the point: `{{client name}}` written three times is ONE
 * question that fills all three places. Nobody wants to type a client's name
 * three times, and asking them to is how a convenience becomes a chore.
 *
 * Past `MAX_BLANKS_PER_PROMPT` the extras are dropped from the list; they stay
 * literal in the body, and the editor says so rather than silently truncating.
 */
export function parseBlanks(body: unknown): string[] {
  const text = typeof body === 'string' ? body : '';
  const seen = new Set<string>();
  const out: string[] = [];
  BLANK_PATTERN.lastIndex = 0;
  let match = BLANK_PATTERN.exec(text);
  while (match !== null) {
    const label = normalizeBlankLabel(match[1]);
    const key = foldCategoryName(label);
    if (label && key && !seen.has(key)) {
      seen.add(key);
      out.push(label);
      if (out.length >= MAX_BLANKS_PER_PROMPT) break;
    }
    match = BLANK_PATTERN.exec(text);
  }
  BLANK_PATTERN.lastIndex = 0;
  return out;
}

export function hasBlanks(body: unknown): boolean {
  return parseBlanks(body).length > 0;
}

/** A prompt with blanks is a template. Derived, so it cannot drift from the body. */
export function promptKind(blanks: readonly string[] | undefined): PromptKind {
  return (blanks?.length ?? 0) > 0 ? 'template' : 'prompt';
}

/**
 * The finished text: every blank replaced by what the person typed for it.
 *
 * AN UNFILLED BLANK BECOMES ITS OWN LABEL, never an empty hole and never the
 * literal `{{client name}}`. The worst outcome this feature could produce is
 * someone sending `{{client name}}` to a client; the second worst is a sentence
 * with a gap where a word should be. A label left in place reads as a sentence
 * the person can see and fix in the draft box before they send it.
 *
 * Values are matched by the SAME fold as the labels, so a person's `Client Name`
 * fills a `{{client name}}` blank. Substitution is literal text — there is no
 * markup path here and this must never invent one.
 */
export function renderPrompt(body: unknown, values: Record<string, unknown> | undefined): string {
  const text = typeof body === 'string' ? body : '';
  const byKey = new Map<string, string>();
  for (const [key, value] of Object.entries(values ?? {})) {
    const folded = foldCategoryName(normalizeBlankLabel(key));
    if (folded) byKey.set(folded, typeof value === 'string' ? value : '');
  }
  BLANK_PATTERN.lastIndex = 0;
  return text.replace(BLANK_PATTERN, (whole, inner: string) => {
    const label = normalizeBlankLabel(inner);
    if (!label) return whole;
    const filled = byKey.get(foldCategoryName(label));
    const trimmed = (filled ?? '').trim();
    return trimmed || label;
  });
}

// ── Normalizing and validating ──────────────────────────────────────────────

export function normalizePromptTitle(raw: unknown): string {
  return boundByCodePoints(
    (typeof raw === 'string' ? raw : '').trim().replace(/\s+/g, ' '),
    PROMPT_TITLE_MAX,
  );
}

/**
 * The body as stored: trailing whitespace off each line and at the end, inner
 * newlines kept exactly as typed. A prompt is prose someone shaped on purpose.
 */
export function normalizePromptBody(raw: unknown): string {
  const text = typeof raw === 'string' ? raw : '';
  return text.replace(/[ \t]+$/gm, '').replace(/\s+$/, '').replace(/^\s+/, '');
}

export function normalizePromptCategory(raw: unknown): string {
  return normalizeCategoryName(raw);
}

/**
 * A title suggested from the body, for the "save what's in the draft" path:
 * the first sentence or line, bounded. Never clever — it is a starting point
 * the person is looking straight at and can overwrite.
 */
export function suggestPromptTitle(body: unknown): string {
  const text = normalizePromptBody(body);
  if (!text) return '';
  const firstLine = text.split('\n', 1)[0] ?? '';
  const sentence = firstLine.split(/(?<=[.!?])\s/, 1)[0] ?? firstLine;
  return normalizePromptTitle(sentence);
}

/** Everything a prompt must satisfy before it is written, in one place. */
export function validatePrompt(draft: SavedPromptDraft): Verdict<NormalizedPrompt> {
  const title = normalizePromptTitle(draft.title);
  if (!title) return { ok: false, problem: 'empty-title' };
  const body = normalizePromptBody(draft.body);
  if (!body) return { ok: false, problem: 'empty-body' };
  if (countCodePoints(body) > PROMPT_BODY_MAX) return { ok: false, problem: 'body-too-long' };
  const blanks = parseBlanks(body);
  return {
    ok: true,
    value: { title, body, category: normalizePromptCategory(draft.category), blanks },
  };
}

/** Whether one more prompt fits. Counting needs the list, so the caller supplies it. */
export function canAddPrompt(count: number, max: number = MAX_PROMPTS_PER_PERSON): Verdict<void> {
  if (!Number.isFinite(count) || count < max) return { ok: true, value: undefined };
  return { ok: false, problem: 'library-full' };
}

/** A fresh prompt id — short, and scoped by the collection it lands in. */
export function newPromptId(nowMs: number = Date.now()): string {
  return `prm_${nowMs.toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

// ── Reading a stored row ────────────────────────────────────────────────────

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * A stored document as the app uses it. Malformed rows come back `null` rather
 * than half-built: a prompt with no body is not a prompt, and rendering one
 * would put an empty draft in front of somebody who asked for their letter.
 */
export function parseSavedPrompt(id: unknown, raw: unknown): SavedPrompt | null {
  const docId = typeof id === 'string' ? id.trim() : '';
  if (!docId || !raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const title = normalizePromptTitle(data.title);
  const body = normalizePromptBody(data.body);
  if (!title || !body) return null;
  const stored = Array.isArray(data.blanks)
    ? (data.blanks as unknown[]).map(normalizeBlankLabel).filter(Boolean)
    : null;
  return {
    id: docId,
    title,
    body,
    category: normalizePromptCategory(data.category),
    // Re-derived when the stored list is missing or unusable, so a row written
    // by an older shape still shows the right blank count.
    blanks: stored && stored.length ? stored.slice(0, MAX_BLANKS_PER_PROMPT) : parseBlanks(body),
    createdAt: num(data.createdAt),
    updatedAt: num(data.updatedAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    lastUsedAt: num(data.lastUsedAt) || undefined,
    useCount: num(data.useCount) || undefined,
    schemaVersion: num(data.schemaVersion) || undefined,
  };
}

/**
 * The picker's order: what you reached for most recently, then what you changed
 * most recently, then by name so two devices never disagree.
 */
export function orderPrompts(prompts: readonly SavedPrompt[]): SavedPrompt[] {
  return [...prompts].sort((a, b) => {
    const used = (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0);
    if (used) return used;
    const touched = b.updatedAt - a.updatedAt;
    if (touched) return touched;
    return a.title.localeCompare(b.title) || (a.id < b.id ? -1 : 1);
  });
}

/**
 * The category list, DERIVED from the prompts rather than kept in a second
 * document — so a category cannot outlive its prompts, an id cannot dangle, and
 * the two can never disagree about what exists.
 *
 * Folded, so "Intake" and "intake" are one group, and the label shown is the
 * first spelling encountered in the ordered list. Alphabetical, with the
 * unfiled group ('') last.
 */
export function promptCategories(prompts: readonly SavedPrompt[]): string[] {
  const byKey = new Map<string, string>();
  for (const prompt of prompts) {
    const name = prompt.category;
    if (!name) continue;
    const key = foldCategoryName(name);
    if (key && !byKey.has(key)) byKey.set(key, name);
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}
